import {
  CORE_EVENT_TYPES,
  CREDIT_EVENT_TYPES,
  CRT_EVENT_TYPES,
  SHARED_ADMIN_EVENT_TYPES,
} from '@repo/utils'
import {
  APP_PERMISSIONS,
  CREDIT_PERMISSIONS,
  CRT_PERMISSIONS,
  type UserPermission,
} from './permissions'
import { RoleName } from './roles'

export const MODULES = ['core', 'crt', 'crt-core', 'credit'] as const
export type Module = (typeof MODULES)[number]

export const SWITCHABLE_MODULES = ['core', 'crt', 'credit'] as const
export type SwitchableModule = (typeof SWITCHABLE_MODULES)[number]

/**
 * Every role belongs to exactly one switchable module. Typed `SwitchableModule` rather than `Module`
 * because `crt-core` describes a *user* holding roles in both modules (see `getUserModule`) and is
 * never the module of a single role.
 */
export const ROLE_MODULES: Record<RoleName, SwitchableModule> = {
  BUSINESS_ADMIN: 'core',
  COMPLIANCE: 'core',
  CUSTOMER_SERVICE: 'core',
  IDT_COMMERCIAL_MARKETING: 'core',
  IDT_SERVICES: 'core',
  INSIDE_SALES: 'core',
  IT_ADMIN: 'core',
  KEY_ACCOUNT: 'core',
  MARKETING_B2B: 'core',
  MARKETING_SYSTEM: 'core',
  OTC: 'core',
  PRICING: 'core',
  PRODUCT_PORTFOLIO: 'core',
  SUPPORT: 'core',
  TMAR: 'core',
  CRT_ADMIN: 'crt',
  CRT_EXECUTIVE: 'crt',
  CRT_USER: 'crt',
  CRT_FINANCIAL_ANALYST: 'crt',
  CREDIT_ADMIN: 'credit',
}

export function getUserModule(roleNames: RoleName[]) {
  const hasCrt = roleNames.some((role) => ROLE_MODULES[role] === 'crt')
  const hasCredit = roleNames.some((role) => ROLE_MODULES[role] === 'credit')
  const hasCore = roleNames.some((role) => ROLE_MODULES[role] === 'core')

  if (hasCrt && hasCore) return 'crt-core'
  if (hasCrt) return 'crt'
  if (hasCredit) return 'credit'
  return 'core'
}

export function getUserAccessibleModules(roleNames: RoleName[]): SwitchableModule[] {
  const modules = new Set<SwitchableModule>()
  for (const role of roleNames) {
    // The `mod` guard is for runtime, not types: a role name read back from the database may not be
    // in the map at all, for instance an obsolete role kept alive by `pendingDeletion`.
    const mod = ROLE_MODULES[role]
    if (mod) {
      modules.add(mod)
    }
  }
  return [...modules]
}

/**
 * The permissions belonging to one module, the counterpart of `getRolesByModule`.
 *
 * `crt-core` covers both, since it describes a user holding roles in each rather than a module a
 * permission can belong to.
 */
export function getPermissionsByModule(module: Module): readonly UserPermission[] {
  switch (module) {
    case 'crt':
      return CRT_PERMISSIONS
    case 'credit':
      return CREDIT_PERMISSIONS
    case 'crt-core':
      return [...APP_PERMISSIONS, ...CRT_PERMISSIONS]
    case 'core':
      return APP_PERMISSIONS
  }
}

export function getRolesByModule(module: Module) {
  return (Object.entries(ROLE_MODULES) as [RoleName, Module][])
    .filter(([, mod]) => mod === module)
    .map(([role]) => role)
}

export function canViewRole(targetRole: RoleName, userModule: Module): boolean {
  const targetModule = ROLE_MODULES[targetRole]

  switch (userModule) {
    // Combined users can see roles of either of their modules
    case 'crt-core':
      return targetModule === 'core' || targetModule === 'crt'
    // Single-module users can only see their own module's roles
    case 'core':
    case 'crt':
    case 'credit':
      return targetModule === userModule
  }
}

export function filterRolesByUserModule(roles: RoleName[], userModule: Module) {
  return roles.filter((role) => canViewRole(role, userModule))
}

export function getVisibleRolesForUser(
  targetUserRoles: RoleName[],
  userModule: Module,
): RoleName[] {
  // Filter to only show roles visible to the viewing user
  return targetUserRoles.filter((role) => canViewRole(role, userModule))
}

export function canViewUserWithRoles(targetUserRoles: RoleName[], userModule: Module): boolean {
  // Check if user can view at least one of the target user's roles
  return targetUserRoles.some((role) => canViewRole(role, userModule))
}

// Audit Trails

/**
 * The event types each module's audit page lists. CRT and Credit include the shared admin events
 * (`activateUser`, `updateRoles`, ...) because their admin screens raise them through their own trail
 * pair, so those rows live in the module's `audit_trails` and nowhere else. Core already lists them.
 */
export const MODULE_EVENT_TYPES: Record<Module, readonly string[]> = {
  core: CORE_EVENT_TYPES,
  crt: [...CRT_EVENT_TYPES, ...SHARED_ADMIN_EVENT_TYPES],
  'crt-core': [...CORE_EVENT_TYPES, ...CRT_EVENT_TYPES],
  credit: [...CREDIT_EVENT_TYPES, ...SHARED_ADMIN_EVENT_TYPES],
}

const CRT_EVENT_TYPE_SET: ReadonlySet<string> = new Set(CRT_EVENT_TYPES)
const CREDIT_EVENT_TYPE_SET: ReadonlySet<string> = new Set(CREDIT_EVENT_TYPES)

/**
 * Which module's `audit_trails` table an event belongs in. Core is the default, so an event type
 * unknown to either module list (a renamed or retired event) stays where it always was.
 *
 * Used by the worker, whose import events each belong to exactly one module, and by the audit
 * trail readers. The backend handlers do not infer: the shared admin events (`activateUser`,
 * `updateRoles`, ...) are core event types that the CRT and Credit admin screens also raise, so each
 * backend module writes through its own `createTrail` pair instead.
 */
export function getEventTypeModule(eventType: string): SwitchableModule {
  if (CRT_EVENT_TYPE_SET.has(eventType)) return 'crt'
  if (CREDIT_EVENT_TYPE_SET.has(eventType)) return 'credit'
  return 'core'
}

export function getModuleEventTypes(userModule: Module) {
  return MODULE_EVENT_TYPES[userModule]
}

export function filterEventTypesByModule(eventTypes: string[] | undefined, userModule: Module) {
  const allowedEventTypes = MODULE_EVENT_TYPES[userModule]

  // If user specified event types, intersect with allowed ones
  if (eventTypes) {
    return eventTypes.filter((et) => allowedEventTypes.includes(et))
  }

  // No filter specified, return all allowed event types for the module
  return [...allowedEventTypes]
}
